<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface Tab {
  label: string
  shortLabel?: string
  value: string
  icon?: string
  image?: string
}

interface Props {
  modelValue: string
  tabs: Tab[]
  variant?: 'default' | 'pill'
}

interface Emits {
  (e: 'update:modelValue', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
})
const emit = defineEmits<Emits>()

const tabsClass = computed(() => `custom-tabs custom-tabs--${props.variant}`)

const tabsRef = ref()
let resizeObserver: ResizeObserver | null = null
let resizeHandler: (() => void) | null = null

function handleTabChange(value: string) {
  emit('update:modelValue', value)
}

async function updateSlidingBackground() {
  await nextTick()

  const tabsElement = tabsRef.value?.$el
  if (!tabsElement) 
    return

  const navElement = tabsElement.querySelector('.el-tabs__nav')
  const activeTab = tabsElement.querySelector('.el-tabs__item.is-active')

  if (!navElement || !activeTab) 
    return

  // Find or create sliding background
  let slidingBg = navElement.querySelector('.sliding-background')
  if (!slidingBg) {
    slidingBg = document.createElement('div')
    slidingBg.className = 'sliding-background'
    navElement.appendChild(slidingBg)
  }

  // Calculate position and size
  const navRect = navElement.getBoundingClientRect()
  const activeRect = activeTab.getBoundingClientRect()

  const left = activeRect.left - navRect.left
  const width = activeRect.width

  // Apply styles
  slidingBg.style.transform = `translateX(${left}px)`
  slidingBg.style.width = `${width}px`
}

// Watch for tab changes
watch(
  () => props.modelValue,
  () => {
    updateSlidingBackground()
  },
  { immediate: true },
)

onMounted(() => {
  // Initial measurements with extra frames to ensure fonts/translations applied
  updateSlidingBackground()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateSlidingBackground()
    })
  })

  const tabsElement = tabsRef.value?.$el
  const navElement = tabsElement?.querySelector('.el-tabs__nav')

  if (navElement && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => {
      updateSlidingBackground()
    })
    resizeObserver.observe(navElement as Element)
  }

  // Window resize listener
  resizeHandler = () => {
    updateSlidingBackground()
  }
  window.addEventListener('resize', resizeHandler)
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    try {
      resizeObserver.disconnect()
    }
    catch (error) {
      // Ignore disconnect errors
      console.warn('ResizeObserver disconnect error:', error)
    }
    resizeObserver = null
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
})
</script>

<template>
  <div>
    <ElTabs
      ref="tabsRef"
      :model-value="modelValue"
      class="flex-row"
      :class="[tabsClass]"
      @update:model-value="handleTabChange"
    >
      <ElTabPane
        v-for="tab in tabs"
        :key="tab.value"
        :label="tab.label"
        :name="tab.value"
      >
        <template #label>
          <div class="tab-label">
            <img
              v-if="tab.image"
              :src="tab.image"
              :alt="tab.label"
              class="tab-image"
            >
            <i v-else-if="tab.icon" :class="tab.icon" class="tab-icon" />
            <template v-if="tab.shortLabel">
              <span class="tab-label-full">{{ tab.label }}</span>
              <span class="tab-label-short">{{ tab.shortLabel }}</span>
            </template>
            <span v-else>{{ tab.label }}</span>
          </div>
        </template>
        <slot :name="tab.value" :tab="tab" />
      </ElTabPane>
    </ElTabs>
  </div>
</template>

<style scoped>
/* Base tab styling */
:deep(.custom-tabs) {
  --el-tabs-header-height: auto;
}

:deep(.custom-tabs .el-tabs__nav-wrap) {
  padding: 0;
  position: relative;
}

:deep(.custom-tabs .el-tabs__nav-wrap::after) {
  display: none;
}

:deep(.custom-tabs .el-tabs__nav) {
  border: none;
  display: flex;
  gap: 0;
  position: relative;
}

:deep(.custom-tabs .el-tabs__active-bar) {
  display: none;
}

.tab-label {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive label swap: full text on desktop, short text on mobile.
 * Only applied when the consumer provides `tab.shortLabel`. */
.tab-label-short {
  display: none;
}

@media (max-width: 640px) {
  .tab-label-full {
    display: none;
  }
  .tab-label-short {
    display: inline;
  }
}

.tab-icon {
  font-size: 16px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-image {
  width: 20px;
  height: 20px;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.custom-tabs .el-tabs__item.is-active .tab-icon) {
  transform: scale(1.1);
}

:deep(.custom-tabs .el-tabs__item.is-active .tab-image) {
  transform: scale(1.1);
}

/* DEFAULT VARIANT - Original styling with sliding background */
:deep(.custom-tabs--default .el-tabs__header) {
  margin: 0;
  border: none;
  background: #fff;
  border-radius: 12px;
  padding: 2px;
  width: fit-content;
  position: relative;
}

/* Sliding background element */
:deep(.custom-tabs--default .sliding-background) {
  position: absolute;
  top: 0;
  left: 0;
  height: calc(100% - 1px);
  background: #efe8fa;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

:deep(.custom-tabs--default .el-tabs__item) {
  border: none !important;
  padding: 6px 16px;
  margin: 0;
  height: 36px;
  line-height: 1.5;
  border-radius: 10px;
  font-weight: 500;
  font-size: 14px;
  color: #303133;
  line-height: 16px;
  background: transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 120px;
  text-align: center;
  position: relative;
  z-index: 2;
  padding-left: 16px !important;
}

:deep(.custom-tabs--default .el-tabs__item:hover) {
  color: rgb(55 65 81);
}

:deep(.custom-tabs--default .el-tabs__item.is-active) {
  color: #9117ef !important;
  background: transparent !important;
}

/* PILL VARIANT - Compact toggle for narrow inline slots (~90-110px) */
:deep(.custom-tabs--pill .el-tabs__header) {
  background: #efe8fa !important;
  border-radius: 8px !important;
  border: 1px solid #d9c8f0 !important;
  padding: 3px !important;
  margin: 0;
  width: fit-content;
  position: relative;
}

:deep(.custom-tabs--pill .sliding-background) {
  position: absolute;
  top: 0;
  left: 0;
  height: calc(100% - 1px);
  background: #9117ef !important;
  border-radius: 6px !important;
  box-shadow: 0 1px 3px rgba(145, 23, 239, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

:deep(.custom-tabs--pill .el-tabs__item) {
  border: none !important;
  padding: 3px 10px !important;
  height: 24px !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  color: #7c3aed !important;
  min-width: 36px !important;
  transition: color 0.2s ease !important;
  margin: 0;
  background: transparent;
  text-align: center;
  position: relative;
  z-index: 2;
  line-height: 1;
}

:deep(.custom-tabs--pill .el-tabs__item:hover) {
  color: #9117ef !important;
}

:deep(.custom-tabs--pill .el-tabs__item.is-active) {
  color: #ffffff !important;
  font-weight: 600 !important;
  background: transparent !important;
}

/* Responsive adjustments for mobile */
@media (max-width: 640px) {
  :deep(.custom-tabs--default .el-tabs__header) {
    border-radius: 10px;
    padding: 2px;
  }

  :deep(.custom-tabs--default .el-tabs__item) {
    padding: 8px 16px !important;
    font-size: 13px;
    min-width: auto;
    height: 32px;
  }

  :deep(.custom-tabs--default .sliding-background) {
    border-radius: 8px;
  }

  .tab-label {
    gap: 4px;
  }

  .tab-icon {
    font-size: 14px;
  }

  .tab-image {
    width: 16px;
    height: 16px;
  }
}
</style>
